import 'ol/ol.css';
import {Vector as VectorSource} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import {Point, Polygon} from "ol/geom";
import {Feature} from "ol";
import {
  Circle as CircleStyle,
  Fill, Stroke, Style
} from "ol/style";
import { AerialMap } from '../aerial/map';
import { textToColor } from './categories';
import { ImageLabelTools } from '.';
import { ImageLabel, LabelCategory } from './image-label';

class Coordinates {
  private coordinates = [];
  private uID;

  constructor(coordinates) {
    this.coordinates = coordinates;
  }

  get id() {
    return this.uID;
  }

  get x() {
    return this.coordinates[0];
  }

  get y() {
    return this.coordinates[1];
  }

  get point() {
    return this.coordinates;
  }

  assignID(id) {
    this.uID = id;
  }

  match(id) {
    return (this.uID === id);
  }

  changeCoordinates(point) {
    if (point.length === 2) {
      this.coordinates = point;
    }
  }
}

class OrthoLabel extends ImageLabel {
  private polygonPoints: Coordinates[] = [];
  private currentPoints: Coordinates[] = [];
  public aerialMap: AerialMap;
  public polygonLayer: VectorLayer;
  public pointLayer: VectorLayer;
  private minCoordinates = 3;

  constructor(category: OrthoCategory, aerialMap: AerialMap) {
    super(category);
    this.aerialMap = aerialMap;
  }

  get hasEnoughPoints() {
    return this.numPoints >= this.minCoordinates;
  }

  get numPoints() {
    return this.polygonPoints.length;
  }

  get currentPointsOnly() {
    return this.currentPoints.map(x => x.point);
  }

  get polygonPointsOnly() {
    return this.polygonPoints.map(x => x.point);
  }

  get coordinates() {
    return this.polygonPoints;
  }

  get tempCoordinates() {
    return this.currentPoints;
  }

  get map() {
    return this.aerialMap.map;
  }

  get isEditable() {
    return this.editing;
  }

  draw() {
    this.drawPoints();
    this.drawPolygon();
  }

  update() {
    this.editPoints();
    this.drawPolygon();
  }

  clear() {
    this.map.removeLayer(this.polygonLayer);
    this.map.removeLayer(this.pointLayer);
  }

  categoryColor(opacity=0.2) {
    return textToColor(this.category.name, opacity);
  }

  pointStyler (opacity=0.8) {
    let pointColor = this.categoryColor(opacity);
    const strokeColor = 'rgba(0, 0, 0, 1.0)';
    let style = new Style({
      image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
          color: strokeColor
        }),
        fill: new Fill({
          color: pointColor
        }),
      })
    });

    return style;
  }

  removeDuplicate() {
    if (this.polygonPoints.length < 2) {
      return;
    }
    const idxEnd = this.polygonPoints.length - 1;
    const xDiff = (Math.abs(this.polygonPoints[idxEnd].x - this.polygonPoints[idxEnd-1].x) < 0.05);
    const yDiff = (Math.abs(this.polygonPoints[idxEnd].y - this.polygonPoints[idxEnd-1].y) < 0.05);
    if (xDiff && yDiff) {
      this.polygonPoints.pop();
    }
  }

  complete() {
    this.completed = true;
    this.editing = false;
    this.removeDuplicate();
    this.currentPoints = [...this.polygonPoints];
    this.drawPoints();
    this.drawPolygon();
  }

  updatePoints() {
    this.currentPoints = [...this.polygonPoints];
  }

  addPoint(point) {
    const newPoint = new Coordinates(point);
    this.polygonPoints.push(newPoint);
  }

  addTempPoint(point) {
    const newPoint = new Coordinates(point);
    this.currentPoints = [...this.polygonPoints];
    this.currentPoints.push(newPoint);
  }

  drawPoints() {
    this.map.removeLayer(this.pointLayer);

    const pointSource = new VectorSource({
      wrapX: false,
      noWrap: true
    });

    const pointStyle = this.pointStyler();

    this.currentPoints.forEach(point => {
      const feat = new Feature({geometry: new Point([point.x, point.y])});
      point.assignID(feat.ol_uid);
      pointSource.addFeature(feat);
    });

    this.pointLayer = new VectorLayer({
      source: pointSource,
      style: pointStyle
    });

    this.map.addLayer(this.pointLayer);
  }

  drawPolygon() {
    this.map.removeLayer(this.polygonLayer);

    const polySource = new VectorSource({
      wrapX: false,
      noWrap: true
    });

    let feature = new Feature({
      geometry: new Polygon([this.currentPointsOnly])
    });

    polySource.addFeature(feature);



    this.polygonLayer = new VectorLayer({
      source: polySource,
      style: new Style({
        fill: new Fill({
          color: this.categoryColor(0.2)
        }),
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.5)',
          width: 2
        }),
      })
    });
    this.map.addLayer(this.polygonLayer);
  }

  editPoints() {
    const features = this.pointLayer.getSource().getFeatures();
    for (let i=0;i<features.length;i++) {
      features[i].getGeometry().setCoordinates(this.currentPoints[i].point);
    }
  }
}

class OrthoCategory extends LabelCategory {
  public labels: OrthoLabel[] = [];
  public aerialMap: AerialMap;

  constructor(name, aerialMap: AerialMap) {
    super(name);
    this.aerialMap = aerialMap;
  }

  addNewLabel() {
    let label = new OrthoLabel(this, this.aerialMap);
    this.labels.push(label);

    return label;
  }
}

export class AerialLabelTools {
  public enabled = false;
  public parent: ImageLabelTools;
  public setPointDelete;
  public setPointAdd;
  public setCategoryList;
  public setAerialTooltipText;

  constructor(parent: ImageLabelTools, props) {
    const {setCategoryList, setAerialTooltipText} = props;

    this.parent = parent;
    this.setCategoryList = setCategoryList;
    this.setAerialTooltipText = setAerialTooltipText;

    this.init();
  }

  get categories() {
    return this.parent.categories
      .filter(x => x instanceof OrthoCategory) as OrthoCategory[];
  }

  get viewer() {
    return this.parent.viewer;
  }

  get aerialMap() {
    return this.viewer.minimap;
  }

  get map() {
    return this.aerialMap.map;
  }

  get activeLabel() {
    let selected = null;
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (label.selected) {
          selected = label;
        }
      });
    });

    return selected;
  }

  get numberLabels() {
    let count = 0;
    this.categories.forEach(category =>
      category.labels.forEach(label => count++));
    return count;
  }

  get moreTooltip() {
    return `
      Click to add more points (${this.numPoints})
      <br>Delete Key to remove last point
      ${(this.numPoints > 1) ? "<br>Double-click to finish" : ""}
      <br>Right-click to remove label
    `;
  }

  get startTooltip() {
    return "Click to start drawing polygon";
  }

  get numPoints() {
    let pointCount = 0;
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          pointCount = label.numPoints;
        }
      })
    );
    return pointCount;
  }

  get selected() {
    let selected = false;
    this.categories.forEach(category =>
      category.labels.forEach(label =>
        selected = (selected || label.selected)
      )
    );

    return selected;
  }

  get editable() {
    let editable = false;
    this.categories.forEach(category =>
      category.labels.forEach(label =>
        editable = (editable || label.isEditable)
      )
    );

    return editable;
  }

  init() {
    // Check for single click
    this.map.on('click', event => {
      if (!this.enabled) {
        return;
      }

      if (this.selected) {
        this.addPoint(event.coordinate);
        this.drawAll();
      }
      else if (this.editable) {
        this.editPoint(event.coordinate);
        this.parent.unSelectLabels();
      }
      else {
        this.map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
          if (feature) {
            if (!this.editable) {
              this.editLayer(feature.ol_uid);
            }
          }
        });
      }
    });

    // Check for mouse move
    this.map.on('pointermove', event => {
      if (!this.enabled || event.dragging) {
        return;
      }

      if (this.selected) {
        this.setToolTipText();
        this.addTempPoint(event.coordinate);
        this.drawAll();
      }
      else if (this.editable) {
        this.editPoint(event.coordinate, true);
        this.editAll();
      } else {
        this.resetToolTipText();
      }
    });

    // Check for double click
    this.map.on('dblclick', event =>{
      if (!this.enabled) {
        return;
      }
      event.preventDefault();

      if (this.selected){
        let label = this.activeLabel;
        this.parent.completeLabel(label.id);
      } else {
        this.resetToolTipText();
      }

    });

    // Check for hotkeys
    window.addEventListener('keydown', event => {
      this.onKeyDown(event);
    }, false);

    // Check for right click
    window.addEventListener('mouseup', event => {
      this.onMouseUp(event);
    }, false);
  }

  setState(state) {
    this.enabled = state;
    this.resetToolTipText();
  }

  onMouseUp(event) {
    if ((event.button === 2) && (this.selected)) {
      this.parent.deleteLabel(this.activeLabel.id);
    }
  }

  onKeyDown(event) {
    // Check for completion hotkey -> Enter
    if (event.which === 13) {
      if (this.selected){
        let label = this.activeLabel;
        this.parent.completeLabel(label.id);
      }
    }

    // Check for deletion hotkey -> Delete
    if (event.which === 46) {
      this.deletePoint();
    }
  }

  setToolTipText() {
    const started = (this.numPoints === 0);
    let helpMsg = started ? this.startTooltip : this.moreTooltip;
    this.setAerialTooltipText(helpMsg);
  }

  resetToolTipText() {
    this.setAerialTooltipText("");
  }

  drawAll() {
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        label.draw();
      });
    });
  }

  editAll() {
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        label.update();
      });
    });
  }

  getNewCategory(category) {
    return new OrthoCategory(category, this.aerialMap);
  }

  deletePoint() {
    const editId = this.editable;
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          label.coordinates.pop();
          label.updatePoints();
          this.setToolTipText();
          this.drawAll();
        }
        else if (editId) {
          const labelToRemove = label.coordinates.filter(coordinate => coordinate.id === editId)[0];
          const idxRemove = label.coordinates.indexOf(labelToRemove);
          label.coordinates.splice(idxRemove, 1);
          this.parent.unSelectLabels();
        }
        else {
          return;
        }
      })
    );

    this.parent.setCategories();
  }

  addPoint(point) {
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          label.addPoint(point);
          this.setToolTipText();
        }
      })
    );

    this.parent.setCategories();
  }

  addTempPoint(point) {
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          label.addTempPoint(point);
        }
      })
    );
  }

  editLayer(uid) {
    this.categories.forEach(category =>
      category.labels.forEach(label =>
        label.coordinates.forEach(point => {
          if (point.match(uid)) {
            label.edit(uid);
          }
        })
      )
    );
  }

  editPoint(point, temp=false) {
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.isEditable) {
          const features = label.pointLayer.getSource().getFeatures();
          if (features) {
            if (!temp){
              const editPoint = label.tempCoordinates.filter(coordinate => coordinate.id === label.isEditable)[0];
              editPoint.changeCoordinates(point);
            } else {
              const editPoint = label.coordinates.filter(coordinate => coordinate.id === label.isEditable)[0];
              editPoint.changeCoordinates(point);
            }
          }
        }
      })
    );
  }
}
